import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../baseQuery';
import {
  ContractType,
  GetContractsQueryParams,
  OfertsType,
  ResponseCustomerType,
  ResponseFilterType,
  ResponseGetContractsType,
  TypeContract,
} from './type';

const getToken = () => localStorage.getItem('token');

export const getAuthorizationHeader = () => {
  const token = getToken();
  return token ? `Bearer ${token}` : '';
};

export const contractsApi = createApi({
  reducerPath: 'contractsApi',
  tagTypes: ['Contracts'],
  refetchOnReconnect: true,
  keepUnusedDataFor: 15,
  baseQuery: baseQueryWithReauth(),
  endpoints: (build) => ({
    getContracts: build.query<ResponseGetContractsType, GetContractsQueryParams>({
      query: ({
        page,
        filter,
        searchValue,
        sort_type,
        types,
        mvd_notifications,
        statuses,
        nationalities,
        categories,
        date_add,
        sort_direction,
      }) => {
        const searchParams = new URLSearchParams();

        searchParams.set('page', page.toString());
        searchParams.set('per_page', '50');

        if (filter) {
          searchParams.set('filter', filter);
        }

        if (types?.length) {
          types.forEach((keyword) => searchParams.append('types[]', keyword));
        }
        if (mvd_notifications?.length) {
          mvd_notifications.forEach((keyword) =>
            searchParams.append('mvd_notifications[]', keyword),
          );
        }
        if (statuses?.length) {
          statuses.forEach((keyword) => searchParams.append('statuses[]', keyword));
        }
        if (nationalities?.length) {
          nationalities.forEach((keyword) =>
            searchParams.append('nationalities[]', keyword),
          );
        }
        if (categories?.length) {
          categories.forEach((keyword) => searchParams.append('categories[]', keyword));
        }

        if (sort_type) {
          searchParams.set('sort_type', sort_type);
        }
        if (sort_direction) {
          searchParams.set('sort_direction', sort_direction);
        }

        if (searchValue) {
          searchParams.set('search', searchValue);
        } else {
          searchParams.delete('search');
        }

        if (date_add?.start) {
          searchParams.set('date_add_start', date_add.start);
        }
        if (date_add?.end) {
          searchParams.set('date_add_end', date_add.end);
        }

        return {
          url: `/customer/docs/unified-contracts?${searchParams.toString()}`,
          method: 'GET',
          headers: {
            Accept: 'application/json',
            Authorization: getAuthorizationHeader(),
          },
        };
      },
      providesTags: () => ['Contracts'],
      serializeQueryArgs: ({ endpointName }) => {
        return endpointName;
      },
      merge: (currentCache, newItems, { arg }) => {
        if (arg.page === 1) {
          return newItems;
        }

        if (currentCache && newItems.data) {
          return {
            ...newItems,
            data: [...(currentCache.data || []), ...newItems.data],
          };
        }
        return newItems;
      },
      forceRefetch({ currentArg, previousArg }) {
        return currentArg !== previousArg;
      },
    }),
    createContract: build.mutation<
      ContractType,
      {
        worker_id: number;
        date_start?: string;
        date_end?: string | null;
        oferta_id?: number | null;
        type?: string | null;
      }
    >({
      query: (body) => ({
        url: '/customer/docs/unified-contracts',
        method: 'POST',
        body,
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    revokeContract: build.mutation<ResponseCustomerType, number>({
      query: (contractId) => ({
        url: `/customer/docs/unified-contracts/${contractId}/revoke`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    terminateContract: build.mutation<ResponseCustomerType, number>({
      query: (contractId) => ({
        url: `/customer/docs/unified-contracts/${contractId}/terminate`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    addContractComment: build.mutation<
      ResponseCustomerType,
      { contractId: number; comment: string }
    >({
      query: ({ contractId, comment }) => ({
        url: `/customer/docs/unified-contracts/${contractId}/comment`,
        method: 'POST',
        body: { comment },
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    getOfertaTypeContracts: build.query<OfertsType[], void>({
      query: () => ({
        url: `/customer/docs/unified-contracts/ofertas`,
        method: 'GET',
      }),
      providesTags: () => ['Contracts'],
    }),
    getTypeContract: build.query<TypeContract[], void>({
      query: () => ({
        url: `/customer/docs/unified-contracts/types`,
        method: 'GET',
      }),
      providesTags: () => ['Contracts'],
    }),
    getFiltersContracts: build.query<ResponseFilterType, void>({
      query: () => ({
        url: `/customer/docs/unified-contracts/filters`,
        method: 'GET',
      }),
      providesTags: () => ['Contracts'],
    }),
    downloadContract: build.mutation<Blob, number>({
      query: (contractId) => ({
        url: `/customer/docs/unified-contracts/${contractId}/download`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    setMvdNotified: build.mutation<ResponseCustomerType, number>({
      query: (contractId) => ({
        url: `/customer/docs/unified-contracts/${contractId}/set-mvd-notified`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    setMvdNotifiedOnTermination: build.mutation<ResponseCustomerType, number>({
      query: (contractId) => ({
        url: `/customer/docs/unified-contracts/${contractId}/set-mvd-notified-on-termination`,
        method: 'POST',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      invalidatesTags: ['Contracts'],
    }),
    getWorkerContracts: build.query<ResponseGetContractsType, number>({
      query: (workerId) => ({
        url: `/customer/docs/unified-contracts/${workerId}`,
        method: 'GET',
        headers: {
          Accept: 'application/json',
          Authorization: getAuthorizationHeader(),
        },
      }),
      providesTags: ['Contracts'],
    }),
  }),
});

export const {
  useGetContractsQuery,
  useCreateContractMutation,
  useRevokeContractMutation,
  useGetOfertaTypeContractsQuery,
  useGetTypeContractQuery,
  useTerminateContractMutation,
  useAddContractCommentMutation,
  useDownloadContractMutation,
  useSetMvdNotifiedMutation,
  useSetMvdNotifiedOnTerminationMutation,
  useGetWorkerContractsQuery,
  useGetFiltersContractsQuery,
} = contractsApi;
